import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Clock, Users, Compass, Globe, ArrowRight, Sparkles, Sailboat, Wine, Heart, Map, Utensils, MoreHorizontal, Bike } from 'lucide-react';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';
import { useLang } from '../i18n';
import { useCurrency } from '../currency';

export const EXPERIENCE_TYPES = [
  { key: 'barca', label: 'In barca', icon: Sailboat },
  { key: 'cantina', label: 'In cantina', icon: Wine },
  { key: 'wellness', label: 'Wellness & SPA', icon: Heart },
  { key: 'tour', label: 'Tour & Guide', icon: Map },
  { key: 'gourmet', label: 'Gourmet', icon: Utensils },
  { key: 'sport', label: 'Sport & Adventure', icon: Bike },
  { key: 'other', label: 'Altro', icon: MoreHorizontal }
];

export default function Experiences() {
  const { slug } = useParams(); // city slug
  const { t } = useLang();
  const { format: formatCurrency } = useCurrency();
  const [typeFilter, setTypeFilter] = useState('');
  const { data: exps, loading } = useFetch(
    `experiences:${slug}`,
    () => publicApi.experiences(slug),
    [slug]
  );
  const { data: city } = useFetch(`city:${slug}`, () => publicApi.city(slug), [slug]);

  // Cover: prefer admin-uploaded photo (services.experiences or matching service_tile),
  // else fall back to Unsplash default.
  const experiencesTile = (city?.service_tiles || []).find(
    t => t?.link_type === 'internal' && t?.link === '/experiences'
  );
  const heroSrc = experiencesTile?.image
    || city?.services?.experiences
    || 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1920&auto=format&fit=crop&q=80';

  // Prices in DB are always in EUR — convert on display via currency context
  const priceLabel = (exp) => {
    if (typeof exp.price !== 'number') return null;
    return formatCurrency(exp.price, { decimals: 0 });
  };

  const filtered = useMemo(() => {
    if (!exps) return [];
    if (!typeFilter) return exps;
    return exps.filter(e => (e.experience_type || 'other') === typeFilter);
  }, [exps, typeFilter]);

  const typeCounts = useMemo(() => {
    const m = {};
    (exps || []).forEach(e => {
      const k = e.experience_type || 'other';
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [exps]);

  const availableTypes = EXPERIENCE_TYPES.filter(t => typeCounts[t.key] > 0);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;

  return (
    <div data-testid={`experiences-list-${slug}`}>
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="relative w-full min-h-[40vh] md:min-h-[45vh] flex items-end">
          <img
            src={resolveImage(heroSrc)}
            alt="YUP Experiences"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black" />
          <div className="relative z-10 max-w-6xl mx-auto px-6 pb-10 pt-24 text-white w-full">
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-black mb-4"
                 style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
              <Sparkles size={10}/> {t('experiences')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Vivi il Garda come mai prima
            </h1>
            <p className="mt-4 text-white/80 text-lg max-w-2xl">
              Pacchetti esclusivi selezionati e venduti da YUP: gite in barca, degustazioni in cantina, tour guidati e molto altro.
            </p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="mb-6 flex items-center gap-3 text-sm text-white/60">
          <Link to={`/${slug}`} className="hover:text-[color:var(--yup-primary)] inline-flex items-center gap-1">
            <ArrowLeft size={14}/> Torna alla città
          </Link>
        </div>

        {/* Type filter chips */}
        {availableTypes.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="experience-type-filters">
            <button
              onClick={() => setTypeFilter('')}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-all ${
                !typeFilter
                  ? 'bg-[color:var(--yup-primary)] text-black shadow-lg'
                  : 'bg-white/[0.04] text-white/70 border border-white/10 hover:border-[color:var(--yup-primary)]/40 hover:text-white'
              }`}
              data-testid="exp-filter-all"
            >
              Tutte
              <span className="opacity-70">{exps?.length || 0}</span>
            </button>
            {availableTypes.map(t => {
              const Icon = t.icon;
              const active = typeFilter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTypeFilter(t.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-all ${
                    active
                      ? 'bg-[color:var(--yup-primary)] text-black shadow-lg'
                      : 'bg-white/[0.04] text-white/70 border border-white/10 hover:border-[color:var(--yup-primary)]/40 hover:text-white'
                  }`}
                  data-testid={`exp-filter-${t.key}`}
                >
                  <Icon size={13}/> {t.label}
                  <span className="opacity-70">{typeCounts[t.key] || 0}</span>
                </button>
              );
            })}
          </div>
        )}

        {(!filtered || filtered.length === 0) ? (
          <div className="text-center py-24 text-white/60">
            <Compass size={48} className="mx-auto mb-4 opacity-30"/>
            <p>{typeFilter ? 'Nessuna experience in questa categoria.' : 'Nessuna experience pubblicata per questa città. Torna presto!'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((exp, idx) => (
              <Link
                key={exp.id}
                to={`/${slug}/experiences/${exp.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[color:var(--yup-primary)]/60 bg-black/40 transition-all fade-in-up"
                style={{ animationDelay: `${0.05 + idx * 0.06}s` }}
                data-testid={`experience-card-${exp.slug}`}
              >
                {exp.image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={resolveImage(exp.image)}
                      alt={exp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                {exp.featured && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-black"
                       style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
                    <Sparkles size={10}/> Top pick
                  </div>
                )}
                {priceLabel(exp) && (
                  <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-black shadow-lg"
                       style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
                    da {priceLabel(exp)}
                  </div>
                )}
                <div className="p-5">
                  {exp.experience_type && (() => {
                    const ty = EXPERIENCE_TYPES.find(t => t.key === exp.experience_type);
                    if (!ty) return null;
                    const Icon = ty.icon;
                    return (
                      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--yup-primary)] font-semibold mb-2">
                        <Icon size={11}/> {ty.label}
                      </div>
                    );
                  })()}
                  <h3 className="text-white font-bold text-lg leading-tight group-hover:text-[color:var(--yup-primary)] transition">{exp.title}</h3>
                  {exp.subtitle && <p className="text-white/60 text-sm mt-2 line-clamp-2">{exp.subtitle}</p>}
                  <div className="mt-4 flex items-center gap-3 text-xs text-white/60">
                    {exp.duration && <span className="inline-flex items-center gap-1"><Clock size={12}/> {exp.duration}</span>}
                    {exp.max_people && <span className="inline-flex items-center gap-1"><Users size={12}/> max {exp.max_people}</span>}
                    {exp.languages?.length > 0 && <span className="inline-flex items-center gap-1"><Globe size={12}/> {exp.languages.map(l => l.toUpperCase()).join(' / ')}</span>}
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-[color:var(--yup-primary)] text-xs tracking-widest uppercase font-semibold">
                    Scopri <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
